import {createContext, useContext, useEffect, useState} from 'react';
import {getUserSessionData, printError, printLog} from "../utils/AppUtils";
import {checkNullArr, checkNullJson, checkNullStr, getJsonValue, getJsonValueFromNestedKeys, isBoolTrue, jsonToStr} from "../../common/fusion-ui/utils/ReactUtils";
import {Keys} from "../config/AppConfig";


const LocalStoreContext = createContext(null);
const TAG = 'LocalStoreProvider';

function LocalStoreProvider({children}) {
    const LS_SUPER_KEY = Keys.lsSessionSuperKey;
    const [localStorageData, setLocalStoreData] = useState(getLocalStoreData());

    useEffect(() => {
        setLocalStoreData(getLocalStoreData());
    }, []);

    function getLocalStoreData() {
        try {
            const data = checkNullJson(getUserSessionData()) ? JSON.parse((localStorage.getItem(LS_SUPER_KEY))) : {};
            // const data = checkNullJson(getUserSessionData()) ? JSON.parse(unscrambleData(localStorage.getItem(LS_SUPER_KEY))) : {};
            return checkNullJson(data) ? data : {};
        } catch (error) {
            logErr('getLocalStoreData:', error);
            return {};
        }
    }

    // Function to update local data
    function updateLocalStoreField(key, value, storePersist = false) {
        // log('updateLocalStoreField.key:', key, 'value:', value);
        try {
            if (checkNullJson(getUserSessionData()) || isBoolTrue(storePersist)) {
                const lsData = getLocalStoreData();
                // log( 'updateLocalStoreField.lsData:', lsData, 'type:', typeof lsData);
                // lsData[key] = isBoolTrue(convertJsonToStr) ? jsonToStr(value) : value;
                lsData[key] = value;
                localStorage.setItem(LS_SUPER_KEY, (jsonToStr(lsData, 0)));
                // localStorage.setItem(LS_SUPER_KEY, scrambleData(jsonToStr(lsData, 0)));
                setLocalStoreData(lsData);
            }
        } catch (error) {
            logErr('updateLocalStoreField.error:', error);
        }
    }

    function getLocalStoreField(key, defValue) {
        // log( 'getLocalStoreField.key:', key, 'defValue:', defValue);
        // return getJsonValue(localStorageData, key, defValue);
        return checkNullArr(key) ? getJsonValueFromNestedKeys(localStorageData, key, defValue) : getJsonValue(localStorageData, key, defValue);
    }

    function checkLocalStoreFieldNullJson(field) {
        return checkNullJson(getLocalStoreField(field));
    }

    function checkLocalStoreFieldNullStr(field) {
        return checkNullStr(getLocalStoreField(field));
    }

    const value = {
        localStorageData,
        updateLocalStoreField,
        getLocalStoreField,
        checkLocalStoreFieldNullJson,
        checkLocalStoreFieldNullStr,
    };

    return <LocalStoreContext.Provider value={value}>
        {children}
    </LocalStoreContext.Provider>
}

function log(...text) {
    printLog(TAG, ...text);
}

function logErr(...text) {
    printError(TAG, ...text);
}

/**
 * A hook to update & get local-storage data.
 * @example
 *     //import hook
 *     import {useLocalStoreData} from "../../../redux/reduxHooks";
 *
 *     //Get local-data and essential methods from useLocalStoreData.
 *     const {
 *         localData,
 *         updateLocalStoreField,
 *         getLocalStoreField
 *     } = useLocalStoreData({
 *         keys: [Fields.searchInputValue, Fields.searchInputValue,],
 *         // defValues: ['', DEF_OPTION,], //EXPERIMENTAL
 *     });
 *
 *     //print local-data on change
 *     useEffect(() => {
 *          console.log('useEffect.localData:', localData);
 *     }, [localData]);
 *
 *     //Declare necessary fields
 *      const Fields = {
 *         searchInputValue: 'inputValue',
 *      };
 *
 *      //Input to demonstrate the use of getLocalFieldValue && updateLocalFieldValue methods
 *       <input
 *          value={getLocalFieldValue(Fields.searchInputValue, DEF_OPTION)}
 *          onChange={(e) => {
 *              updateLocalFieldValue(Fields.searchInputValue, e.target.value);
 *          }}/>
 *
 *     //Function to update local-field value.
 *     function updateLocalFieldValue(field, value) {
 *         updateLocalStoreField(field, value);
 *     }
 *
 *     //Function to get local-field value.
 *     function getLocalFieldValue(field, defValue) {
 *         return getLocalStoreField(field, defValue);
 *     }
 */
function useLocalStoreData() {
    return useContext(LocalStoreContext);
}

export {LocalStoreProvider, useLocalStoreData};
